/**
 * AppStatsView Dedicated Icon Cache & Resolution Engine (V2)
 *
 * Enforces strict cache discipline:
 * - Purges all legacy icon & metadata caches on startup
 * - Maintains APPSTATSVIEW_ICON_CACHE_V2
 * - NEVER caches fallbacks or monograms
 * - Caches ONLY verified remote artwork URLs after successful load with naturalWidth > 0
 * - Retains Last Known Good artwork across state updates, re-renders, and navigations
 */

export const ICON_CACHE_VERSION_KEY = 'APPSTATSVIEW_ICON_CACHE_V2';

export interface VerifiedIconEntry {
  appStoreId: string;
  iconUrl: string;
  verifiedLoaded: boolean;
  naturalWidth: number;
  naturalHeight: number;
  timestamp: number;
}

// In-memory runtime Last Known Good store
const runtimeLastKnownGoodMap = new Map<string, VerifiedIconEntry>();

// Global in-memory set of verified loaded URLs (prevents image re-flicker during React renders)
export const VERIFIED_LOADED_URLS = new Set<string>();

/**
 * Known Apple App Store 512x512 High-Res Official Artwork Registry
 * Keyed by: appStoreId, slug, and canonical app names
 */
export const KNOWN_APPLE_ARTWORKS: Record<string, string> = {
  // Duolingo
  '570060128': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',
  'duolingo': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/67/e4/51/67e4516e-c1d0-452b-3f3e-5def33df3138/AppIcon-0-0-1x_U007epad-0-1-85-220.png/512x512bb.jpg',

  // ChatGPT
  '6448311069': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',
  'chatgpt': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/62/11/63/6211631a-f4ca-980d-ccdc-ac8f052e4662/AppIcon-0-0-1x_U007epad-0-0-0-1-0-P3-85-220.png/512x512bb.jpg',

  // Spotify
  '324684580': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',
  'spotify': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/78/d3/2a/78d32a70-87dc-58e9-86c7-04b87c88f873/AppIcon-0-0-1x_U007epad-0-1-0-0-sRGB-85-220.png/512x512bb.jpg',

  // TikTok
  '835599320': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
  'tiktok': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/b4/f6/9e/b4f69e23-a20c-784c-7f41-400fd8ab3d1c/TikTok_AppIcon26-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',

  // Canva
  '897446215': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',
  'canva': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/ad/8a/a8/ad8aa8cf-734e-96bc-162a-2cec006ffc12/AppIcon-0-0-1x_U007epad-0-11-0-85-220.png/512x512bb.jpg',

  // CapCut
  '1500855883': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/66/e5/e566e522-06cc-b4a9-c541-cfa8c35b8540/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.jpg',
  'capcut': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/e5/66/e5/e566e522-06cc-b4a9-c541-cfa8c35b8540/AppIcon-0-0-1x_U007emarketing-0-8-0-85-220.png/512x512bb.jpg',

  // Notion
  '1232780281': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',
  'notion': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/3f/84/6a/3f846aa0-0597-016c-bd4f-3a7c44843a99/AppIconProd-0-0-1x_U007epad-0-0-0-1-0-0-P3-85-220.png/512x512bb.jpg',

  // Instagram
  '389801252': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
  'instagram': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/23/59/e9/2359e92d-376c-cc29-b9e6-ab9a4a00fcf4/Prod-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',

  // Babbel
  '829587759': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/c9/38/eb/c938eb21-0770-fe08-1059-e9eb3409b2b9/AppIcon-Default-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',
  'babbel': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/c9/38/eb/c938eb21-0770-fe08-1059-e9eb3409b2b9/AppIcon-Default-0-0-1x_U007epad-0-1-0-85-220.png/512x512bb.jpg',

  // Busuu
  '379968583': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a0/04/58/a00458dd-f001-30a4-247c-fda5f3adf4e3/AppIcon-0-0-1x_U007emarketing-0-8-0-sRGB-0-0-85-220.png/512x512bb.jpg',
  'busuu': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/a0/04/58/a00458dd-f001-30a4-247c-fda5f3adf4e3/AppIcon-0-0-1x_U007emarketing-0-8-0-sRGB-0-0-85-220.png/512x512bb.jpg',

  // Memrise
  '635966718': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/55/48/d4/5548d47f-d6cc-4f35-eb13-5fa69872e717/AppIcon-0-0-1x_U007emarketing-0-8-0-P3-0-85-220.png/512x512bb.jpg',
  'memrise': 'https://is1-ssl.mzstatic.com/image/thumb/Purple221/v4/55/48/d4/5548d47f-d6cc-4f35-eb13-5fa69872e717/AppIcon-0-0-1x_U007emarketing-0-8-0-P3-0-85-220.png/512x512bb.jpg',

  // Rosetta Stone
  '435588892': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
  'rosetta stone': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg',
  'rosetta-stone': 'https://is1-ssl.mzstatic.com/image/thumb/Purple211/v4/cb/06/60/cb066089-c26c-cec2-001d-46d18faae1c5/RosettaStoneClassic-0-0-1x_U007epad-0-1-0-sRGB-85-220.png/512x512bb.jpg'
};

// Seed known artworks into verified URL set
Object.values(KNOWN_APPLE_ARTWORKS).forEach((url) => VERIFIED_LOADED_URLS.add(url));

/**
 * Purge all legacy caches from previous versions
 */
export function purgeOldIconCaches(): void {
  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    const keysToRemove: string[] = [];
    for (let i = 0; i < localStorage.length; i++) {
      const key = localStorage.key(i);
      if (!key) continue;

      // Invalidate old metadata & icon caches, but keep user data (watchlist, collections, alerts, user)
      if (
        key !== ICON_CACHE_VERSION_KEY &&
        (key.startsWith('appstatsview_app_meta_') ||
          key.startsWith('appstatsview_icon_') ||
          key.startsWith('appstatsview_verified_') ||
          key.includes('iconCache') ||
          key.includes('metadataCache') ||
          key.includes('resolvedIcons'))
      ) {
        keysToRemove.push(key);
      }
    }

    keysToRemove.forEach((k) => {
      try {
        localStorage.removeItem(k);
      } catch (e) {}
    });

    if (keysToRemove.length > 0) {
      console.log(`[IconCache] Purged ${keysToRemove.length} legacy icon cache entries.`);
    }
  } catch (e) {
    // Ignore localStorage errors
  }
}

/**
 * Load V2 persistent icon cache on startup
 */
function initializeV2Cache(): void {
  purgeOldIconCaches();

  if (typeof window === 'undefined' || !window.localStorage) return;

  try {
    const raw = localStorage.getItem(ICON_CACHE_VERSION_KEY);
    if (raw) {
      const parsed: Record<string, VerifiedIconEntry> = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        Object.entries(parsed).forEach(([key, entry]) => {
          if (
            entry &&
            entry.iconUrl &&
            entry.verifiedLoaded &&
            isRemoteArtworkUrl(entry.iconUrl) &&
            entry.naturalWidth > 0
          ) {
            runtimeLastKnownGoodMap.set(key, entry);
            VERIFIED_LOADED_URLS.add(entry.iconUrl);
          }
        });
      }
    }
  } catch (e) {
    // Ignore JSON parse errors
  }
}

// Auto-run initialization
initializeV2Cache();

/**
 * Validate that a URL is a real remote artwork URL (never a monogram, gradient, local or fallback)
 */
export function isRemoteArtworkUrl(url: string | null | undefined): boolean {
  if (!url || typeof url !== 'string') return false;
  const trimmed = url.trim().toLowerCase();
  if (
    trimmed.startsWith('data:') ||
    trimmed.startsWith('blob:') ||
    trimmed.length < 10 ||
    trimmed.endsWith('.svg') ||
    trimmed === 'd' ||
    trimmed === 'c' ||
    trimmed === 't' ||
    trimmed === 'n' ||
    trimmed === 's'
  ) {
    return false;
  }
  return trimmed.startsWith('http://') || trimmed.startsWith('https://');
}

/**
 * Retrieve Last Known Good artwork URL for an appStoreId or slug key
 */
export function getLastKnownGoodIcon(appStoreIdOrKey: string | number | null | undefined): string | null {
  if (!appStoreIdOrKey) return null;
  const key = String(appStoreIdOrKey).trim().toLowerCase();

  // 1. Check in-memory Last Known Good map
  const entry = runtimeLastKnownGoodMap.get(key);
  if (entry && entry.iconUrl && entry.verifiedLoaded) {
    return entry.iconUrl;
  }

  // 2. Check KNOWN_APPLE_ARTWORKS registry
  if (KNOWN_APPLE_ARTWORKS[key]) {
    return KNOWN_APPLE_ARTWORKS[key];
  }

  return null;
}

/**
 * Save Last Known Good icon ONLY after real image onLoad verification
 * (naturalWidth > 0 and valid remote URL)
 */
export function saveLastKnownGoodIcon(
  appStoreIdOrKey: string | number,
  iconUrl: string,
  naturalWidth: number,
  naturalHeight: number
): void {
  if (!appStoreIdOrKey || !isRemoteArtworkUrl(iconUrl)) return;
  if (naturalWidth <= 0 || naturalHeight <= 0) return;

  const key = String(appStoreIdOrKey).trim().toLowerCase();
  const entry: VerifiedIconEntry = {
    appStoreId: key,
    iconUrl,
    verifiedLoaded: true,
    naturalWidth,
    naturalHeight,
    timestamp: Date.now()
  };

  // 1. Save to in-memory runtime store
  runtimeLastKnownGoodMap.set(key, entry);
  VERIFIED_LOADED_URLS.add(iconUrl);

  // 2. Persist to localStorage V2 cache
  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = localStorage.getItem(ICON_CACHE_VERSION_KEY);
      const cache: Record<string, VerifiedIconEntry> = raw ? JSON.parse(raw) : {};
      cache[key] = entry;
      localStorage.setItem(ICON_CACHE_VERSION_KEY, JSON.stringify(cache));
    } catch (e) {
      // Ignore quota errors
    }
  }
}

/**
 * Invalidate a failing remote artwork URL from the cache
 */
export function invalidateBadIconUrl(appStoreIdOrKey: string | number, failedUrl?: string): void {
  if (!appStoreIdOrKey) return;
  const key = String(appStoreIdOrKey).trim().toLowerCase();

  runtimeLastKnownGoodMap.delete(key);
  if (failedUrl) {
    VERIFIED_LOADED_URLS.delete(failedUrl);
  }

  if (typeof window !== 'undefined' && window.localStorage) {
    try {
      const raw = localStorage.getItem(ICON_CACHE_VERSION_KEY);
      if (raw) {
        const cache: Record<string, VerifiedIconEntry> = JSON.parse(raw);
        if (cache[key]) {
          delete cache[key];
          localStorage.setItem(ICON_CACHE_VERSION_KEY, JSON.stringify(cache));
        }
      }
    } catch (e) {}
  }
}

/**
 * Canonical Icon Resolution Function
 *
 * Strict Priority Order:
 * 1. app.lastKnownGoodIconUrl / getLastKnownGoodIcon(storeKey)
 * 2. app.iconUrl
 * 3. app.icons?.primary
 * 4. app.artworkUrl512
 * 5. app.artworkUrl100
 * 6. app.storeIconUrl / app.appStoreIconUrl / app.googlePlayIconUrl
 * 7. KNOWN_APPLE_ARTWORKS lookup by storeId or slug
 *
 * Returns null if none exist. Never returns fallbacks or monograms.
 */
export function resolveAppIcon(app?: any | null): string | null {
  if (!app) return null;

  const storeId = app.appStoreId ? String(app.appStoreId).trim() : '';
  const slugId = (app.slug || app.id ? String(app.slug || app.id) : '').trim().toLowerCase();
  const nameId = (app.name || '').trim().toLowerCase();

  // 1. Last Known Good URL
  if (app.lastKnownGoodIconUrl && isRemoteArtworkUrl(app.lastKnownGoodIconUrl)) {
    return app.lastKnownGoodIconUrl;
  }
  if (storeId) {
    const lkg = getLastKnownGoodIcon(storeId);
    if (lkg && isRemoteArtworkUrl(lkg)) return lkg;
  }
  if (slugId) {
    const lkg = getLastKnownGoodIcon(slugId);
    if (lkg && isRemoteArtworkUrl(lkg)) return lkg;
  }

  // 2. Canonical iconUrl
  if (app.iconUrl && isRemoteArtworkUrl(app.iconUrl)) {
    return app.iconUrl.trim();
  }

  // 3. icons?.primary
  if (app.icons && typeof app.icons === 'object' && isRemoteArtworkUrl(app.icons.primary)) {
    return app.icons.primary.trim();
  }

  // 4. artworkUrl512
  if (app.artworkUrl512 && isRemoteArtworkUrl(app.artworkUrl512)) {
    return app.artworkUrl512.trim();
  }

  // 5. artworkUrl100
  if (app.artworkUrl100 && isRemoteArtworkUrl(app.artworkUrl100)) {
    return app.artworkUrl100.trim();
  }

  // 6. storeIconUrl / appStoreIconUrl / googlePlayIconUrl
  if (app.storeIconUrl && isRemoteArtworkUrl(app.storeIconUrl)) {
    return app.storeIconUrl.trim();
  }
  if (app.appStoreIconUrl && isRemoteArtworkUrl(app.appStoreIconUrl)) {
    return app.appStoreIconUrl.trim();
  }
  if (app.googlePlayIconUrl && isRemoteArtworkUrl(app.googlePlayIconUrl)) {
    return app.googlePlayIconUrl.trim();
  }

  // 7. Known Apple Artworks Registry matching
  for (const key of [storeId, slugId, nameId]) {
    if (key && KNOWN_APPLE_ARTWORKS[key] && isRemoteArtworkUrl(KNOWN_APPLE_ARTWORKS[key])) {
      return KNOWN_APPLE_ARTWORKS[key];
    }
  }

  return null;
}
